import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Inject,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  UntypedFormBuilder,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatLegacyCheckboxChange } from '@angular/material/legacy-checkbox';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialog,
  MatLegacyDialogRef,
} from '@angular/material/legacy-dialog';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { StepTriggerComponent } from '@app/features/pricing-and-availability';
import { PropertyRuleset, RuleSetDialogAction, RuleSetDialogMode } from '@app/features/property-rulesets/property-ruleset.interface';
import { PropertyRulesetService } from '@app/features/property-rulesets/property-ruleset.service';
import { FunctionPipe } from '@app/shared/pipes/function/function.pipe';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { UiModule } from '@app/ui';
import { ColorPickerComponent } from '@app/ui/color-picker/color-picker.component';
import { FormLabelComponent } from '@app/ui/form/form-label/form-label.component';
import { NestedFullScreenModalComponent } from '@app/ui/nested-full-screen-modal/nested-full-screen-modal.component';
import { TranslocoModule } from '@jsverse/transloco';
import { CollapsibleCardComponent, CollapsibleCardContentDirective, IconComponent } from '@ui';

type RulesetFormAreaKey = 'seasonal_adjustment' | 'min_max_stay' | 'restricted_check_in_out' | 'discounts';

@Component({
  selector: 'sbnb-ruleset-form',
  templateUrl: './ruleset-form.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      // We need to find a better way to handle this - suffixs seem globally placed, and its inconsistent
      :host ::ng-deep .mat-form-field-suffix {
        top: 0 !important;
      }
    `,
  ],
  // TODO: Move StepTrigger to a new UI component
  imports: [
    CommonModule,
    UiModule,
    IconComponent,
    ReactiveFormsModule,
    FormLabelComponent,
    TranslocoModule,
    StepTriggerComponent,
    FunctionPipe,
    CollapsibleCardComponent,
    CollapsibleCardContentDirective,
    ColorPickerComponent,
    NestedFullScreenModalComponent,
  ],
})
export class RulesetFormComponent implements OnInit {
  private propertyRulesetService = inject(PropertyRulesetService);
  private formBuilder = inject(UntypedFormBuilder);
  private toast = inject(ToastNotificationsService);
  private destroyRef = inject(DestroyRef);
  private dialog = inject(MatLegacyDialog);
  private dialogRef = inject(MatLegacyDialogRef<RulesetFormComponent>);
  private authService = inject(AuthenticationService);
  public weekdays: number[] = [];

  uuid = signal<string | null>(null);
  mode = computed(() => (this.uuid() ? 'edit' : (this.dialogData.mode === RuleSetDialogMode.APPLY ? 'apply' : 'create')));
  ruleset = signal<PropertyRuleset | null>(null);

  daysOfWeekMapping = this.propertyRulesetService.DAYS_OF_WEEK_MAPPING_MIN_NIGHTS_BY_DAY;
  daysOfWeekKeys = Object.keys(this.daysOfWeekMapping); // Expose keys for template access

  isLoading = computed(() => this.uuid() && this.ruleset() === null);
  isError = signal(false);
  isSaving = signal(false);
  isDeleting = signal(false);

  modeLabelMapper = {
    apply: 'Apply',
    create: 'Create',
    edit: 'Apply changes',
  };

  RuleSetDialogAction = RuleSetDialogAction;

  constructor(@Inject(MAT_DIALOG_DATA) public dialogData: { rulesetId: string, mode: RuleSetDialogMode }) { }

  ngOnInit() {
    const preference = this.authService.getStartOfWeekPreference();
    console.log('preference', preference);
    if (preference === 0) {
      this.weekdays = [0, 1, 2, 3, 4, 5, 6];

    } else {
      this.weekdays = [1, 2, 3, 4, 5, 6, 0];
    }

    if (this.dialogData.rulesetId) {
      this.uuid.set(this.dialogData.rulesetId);
    }

    if (this.uuid()) {
      this.propertyRulesetService.getCalendarRuleset(this.uuid()).subscribe({
        next: (ruleset) => {
          this.ruleset.set(ruleset);
          this.loadValuesToForm();
        },
        error: () => {
          this.isError.set(true);
        },
      });
    } else {
      this.ruleset.set(this.propertyRulesetService.NEW_RULESET_DEFAULT_VALUES);
      this.loadValuesToForm();
    }
  }

  private createLengthOfStayDiscountFormGroup(
    initialNights: number | null = null,
    initialValue: number | null = null
  ): FormGroup {
    return this.formBuilder.group(
      {
        nights: [initialNights, [Validators.min(1), Validators.pattern(/^\d*$/)]],
        value: [initialValue],
      },
      { validators: lengthOfStayDiscountValidator }
    );
  }

  private createLastMinuteDiscountFormGroup(
    initialDays: number | null = null,
    initialValue: number | null = null
  ): FormGroup {
    return this.formBuilder.group({
      days: [initialDays, [Validators.min(1), Validators.pattern(/^\d*$/)]],
      value: [initialValue],
    });
  }

  private createEarlyBirdDiscountFormGroup(
    initialMonths: number | null = null,
    initialValue: number | null = null
  ): FormGroup {
    return this.formBuilder.group({
      months: [initialMonths, [Validators.min(1), Validators.pattern(/^\d*$/)]],
      value: [initialValue],
    });
  }

  rulesetForm = this.formBuilder.group({
    name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(256)]],
    color: [0, [Validators.min(0), Validators.max(7)]],

    seasonal_adjustment: this.formBuilder.group({
      value: [null, [Validators.min(0), Validators.max(9999), Validators.pattern(/^\d*$/)]],
      type: ['increase', [Validators.required]],
      min_price: [null, [Validators.min(1), Validators.pattern(/^\d*$/)]],
      max_price: [null, [Validators.min(1), Validators.pattern(/^\d*$/)]],
    }),
    min_max_stay: this.formBuilder.group(
      {
        min: [null, [Validators.min(0), Validators.max(9999), Validators.pattern(/^\d*$/)]],
        max: [null, [Validators.min(0), Validators.max(9999), Validators.pattern(/^\d*$/)]],
        min_nights_by_day: this.formBuilder.array(
          Object.keys(this.daysOfWeekMapping).map(
            () => new FormControl(null, [Validators.min(0), Validators.pattern(/^\d*$/)])
          )
        ),
      },
      { validators: minMaxStayValidator }
    ),
    restricted_check_in_out: this.formBuilder.group({
      check_in: this.formBuilder.array([] as number[]),
      check_out: this.formBuilder.array([] as number[]),
    }),
    discounts: this.formBuilder.group({
      length_of_stay: this.formBuilder.array([]),
      last_minute: this.formBuilder.array([]),
      early_bird: this.formBuilder.array([]),
    }),
  });

  activeFormArea = signal<RulesetFormAreaKey | null>(null);

  // This is the original data for the active form area
  // If the user cancels on a sub area, we replace the data with the original
  formAreaOriginalData = signal<unknown>(null);

  // Helper getter for easier access in the template
  get minNightsByDayControls() {
    return (this.rulesetForm.get('min_max_stay.min_nights_by_day') as FormArray).controls;
  }

  // Helper getter for length of stay discounts
  get lengthOfStayDiscountControls() {
    return (this.rulesetForm.get('discounts.length_of_stay') as FormArray).controls;
  }

  // Helper getter for last minute discounts
  get lastMinuteDiscountControls() {
    return (this.rulesetForm.get('discounts.last_minute') as FormArray).controls;
  }

  // Helper getter for early bird discounts
  get earlyBirdDiscountControls() {
    return (this.rulesetForm.get('discounts.early_bird') as FormArray).controls;
  }

  addLengthOfStayDiscount(): void {
    const lengthOfStayArray = this.rulesetForm.get('discounts.length_of_stay') as FormArray;
    lengthOfStayArray.push(this.createLengthOfStayDiscountFormGroup());
  }

  removeLengthOfStayDiscount(index: number): void {
    const lengthOfStayArray = this.rulesetForm.get('discounts.length_of_stay') as FormArray;
    lengthOfStayArray.removeAt(index);

    this.rulesetForm.markAsDirty();
  }

  addLastMinuteDiscount(): void {
    const lastMinuteArray = this.rulesetForm.get('discounts.last_minute') as FormArray;
    lastMinuteArray.push(this.createLastMinuteDiscountFormGroup());
  }

  removeLastMinuteDiscount(index: number): void {
    const lastMinuteArray = this.rulesetForm.get('discounts.last_minute') as FormArray;
    lastMinuteArray.removeAt(index);

    this.rulesetForm.markAsDirty();
  }

  addEarlyBirdDiscount(): void {
    const earlyBirdArray = this.rulesetForm.get('discounts.early_bird') as FormArray;
    earlyBirdArray.push(this.createEarlyBirdDiscountFormGroup());
  }

  removeEarlyBirdDiscount(index: number): void {
    const earlyBirdArray = this.rulesetForm.get('discounts.early_bird') as FormArray;
    earlyBirdArray.removeAt(index);

    this.rulesetForm.markAsDirty();
  }

  openSection(area: RulesetFormAreaKey) {
    this.activeFormArea.set(area);
    this.formAreaOriginalData.set(Object.assign({}, this.rulesetForm.get(area)?.value));
  }

  // Clears the active form area
  // Also resets the form data to the original, unedited data before the area was opened
  resetActiveFormAreaAndClearUnsavedChanges(area: RulesetFormAreaKey) {
    const originalDataForArea = this.formAreaOriginalData();

    if (area === 'restricted_check_in_out' && originalDataForArea) {
      const restrictedGroup = this.rulesetForm.get('restricted_check_in_out') as FormGroup;
      const originalCheckInData = (originalDataForArea as { check_in?: number[] }).check_in || [];
      const originalCheckOutData = (originalDataForArea as { check_out?: number[] }).check_out || [];

      restrictedGroup.setControl(
        'check_in',
        new FormArray(originalCheckInData.map((val: number) => new FormControl(val))),
        { emitEvent: false }
      );
      restrictedGroup.setControl(
        'check_out',
        new FormArray(originalCheckOutData.map((val: number) => new FormControl(val))),
        { emitEvent: false }
      );
    } else if (area === 'discounts' && originalDataForArea) {
      const discountsGroup = this.rulesetForm.get('discounts') as FormGroup;

      // Handle Length of Stay Discounts
      const lengthOfStayArray = discountsGroup.get('length_of_stay') as FormArray;
      const originalLengthOfStayData =
        (originalDataForArea as { length_of_stay?: { nights: number | null; value: number | null }[] })
          .length_of_stay || [];

      lengthOfStayArray.clear({ emitEvent: false });

      if (originalLengthOfStayData.length > 0) {
        originalLengthOfStayData.forEach((discount) => {
          lengthOfStayArray.push(this.createLengthOfStayDiscountFormGroup(discount.nights, discount.value), {
            emitEvent: false,
          });
        });
      }

      // Handle Last Minute Discounts
      const lastMinuteArray = discountsGroup.get('last_minute') as FormArray;
      const originalLastMinuteData =
        (originalDataForArea as { last_minute?: { days: number | null; value: number | null }[] }).last_minute || [];

      lastMinuteArray.clear({ emitEvent: false });

      if (originalLastMinuteData.length > 0) {
        originalLastMinuteData.forEach((discount) => {
          lastMinuteArray.push(this.createLastMinuteDiscountFormGroup(discount.days, discount.value), {
            emitEvent: false,
          });
        });
      }

      // Handle Early Bird Discounts
      const earlyBirdArray = discountsGroup.get('early_bird') as FormArray;
      const originalEarlyBirdData =
        (originalDataForArea as { early_bird?: { months: number | null; value: number | null }[] }).early_bird || [];

      earlyBirdArray.clear({ emitEvent: false });

      if (originalEarlyBirdData.length > 0) {
        originalEarlyBirdData.forEach((discount) => {
          earlyBirdArray.push(this.createEarlyBirdDiscountFormGroup(discount.months, discount.value), {
            emitEvent: false,
          });
        });
      }
    } else if (originalDataForArea) {
      const formControl = this.rulesetForm.get(area);
      if (formControl) {
        formControl.patchValue(originalDataForArea, { emitEvent: false });
      }
    }

    this.activeFormArea.set(null);
    this.formAreaOriginalData.set(null);
  }

  // We can just clear the section data here, since everything else is done already for us
  saveSectionChanges() {
    this.activeFormArea.set(null);
    this.formAreaOriginalData.set(null);
  }

  private loadValuesToForm() {
    const ruleset = this.ruleset();
    if (!ruleset) return;

    this.rulesetForm.patchValue(
      {
        name: ruleset.name,
        color: ruleset.color,
        seasonal_adjustment: ruleset.seasonal_adjustment,
        min_max_stay: {
          min: ruleset.min_max_stay?.min,
          max: ruleset.min_max_stay?.max,
        },
        // discounts will be handled manually below
      },
      { emitEvent: false }
    );

    // Ensure FormArray exists before patching for min_nights_by_day
    const minNightsByDayArray = this.rulesetForm.get('min_max_stay.min_nights_by_day') as FormArray;
    if (minNightsByDayArray && ruleset.min_max_stay?.min_nights_by_day) {
      ruleset.min_max_stay.min_nights_by_day.forEach((daySetting, index) => {
        if (minNightsByDayArray.controls[index]) {
          minNightsByDayArray.controls[index].patchValue(daySetting.value, { emitEvent: false });
        }
      });
    }

    // Properly load restricted_check_in_out FormArrays using setControl
    const restrictedCheckInOutData = ruleset.restricted_check_in_out;
    if (restrictedCheckInOutData) {
      const restrictedGroup = this.rulesetForm.get('restricted_check_in_out') as FormGroup;
      const checkInData = restrictedCheckInOutData.check_in || [];
      const checkOutData = restrictedCheckInOutData.check_out || [];

      restrictedGroup.setControl(
        'check_in',
        new FormArray(checkInData.map((dayIndex: number) => new FormControl(dayIndex))),
        { emitEvent: false }
      );
      restrictedGroup.setControl(
        'check_out',
        new FormArray(checkOutData.map((dayIndex: number) => new FormControl(dayIndex))),
        { emitEvent: false }
      );
    }

    // Handle length_of_stay discounts
    const lengthOfStayArray = this.rulesetForm.get('discounts.length_of_stay') as FormArray;
    lengthOfStayArray.clear({ emitEvent: false }); // Clear existing/default control

    if (ruleset.discounts?.length_of_stay?.length) {
      ruleset.discounts.length_of_stay.forEach((discount) => {
        lengthOfStayArray.push(this.createLengthOfStayDiscountFormGroup(discount.nights, discount.value), {
          emitEvent: false,
        });
      });
    }

    // Handle last_minute discounts
    const lastMinuteArray = this.rulesetForm.get('discounts.last_minute') as FormArray;
    lastMinuteArray.clear({ emitEvent: false }); // Clear existing/default control

    if (ruleset.discounts?.last_minute?.length) {
      ruleset.discounts.last_minute.forEach((discount) => {
        lastMinuteArray.push(this.createLastMinuteDiscountFormGroup(discount.days, discount.value), {
          emitEvent: false,
        });
      });
    }

    // Handle early_bird discounts
    const earlyBirdArray = this.rulesetForm.get('discounts.early_bird') as FormArray;
    earlyBirdArray.clear({ emitEvent: false }); // Clear existing/default control

    if (ruleset.discounts?.early_bird?.length) {
      ruleset.discounts.early_bird.forEach((discount) => {
        earlyBirdArray.push(this.createEarlyBirdDiscountFormGroup(discount.months, discount.value), {
          emitEvent: false,
        });
      });
    }
  }

  private updateRulesetFromFormValues() {
    const minNightsByDayValues = (this.rulesetForm.get('min_max_stay.min_nights_by_day') as FormArray).controls.map(
      (control, index) => ({
        day: index,
        value: control.value !== '' ? Number(control.value) || null : null, // Convert empty string or invalid number to null
      })
    );

    this.ruleset.update((ruleset) => ({
      ...ruleset,
      name: this.rulesetForm.get('name').value,
      color: this.rulesetForm.get('color').value,
      seasonal_adjustment: this.rulesetForm.get('seasonal_adjustment').value,
      min_max_stay: {
        min:
          this.rulesetForm.get('min_max_stay.min').value !== ''
            ? Number(this.rulesetForm.get('min_max_stay.min').value) || null
            : null,
        max:
          this.rulesetForm.get('min_max_stay.max').value !== ''
            ? Number(this.rulesetForm.get('min_max_stay.max').value) || null
            : null,
        min_nights_by_day: minNightsByDayValues,
      },
      restricted_check_in_out: {
        check_in: this.rulesetForm.get('restricted_check_in_out.check_in').value,
        check_out: this.rulesetForm.get('restricted_check_in_out.check_out').value,
      },
      discounts: {
        length_of_stay: this.rulesetForm.get('discounts.length_of_stay').value.filter(
          // Filter out empty/null discount rows before saving
          (discount: { nights: number | null; value: number | null }) =>
            discount.nights !== null || discount.value !== null
        ),
        last_minute: this.rulesetForm.get('discounts.last_minute').value.filter(
          // Filter out empty/null discount rows before saving
          (discount: { days: number | null; value: number | null }) => discount.days !== null || discount.value !== null
        ),
        early_bird: this.rulesetForm.get('discounts.early_bird').value.filter(
          // Filter out empty/null discount rows before saving
          (discount: { months: number | null; value: number | null }) =>
            discount.months !== null || discount.value !== null
        ),
      },
    }));
  }

  save() {
    if (!this.rulesetForm.valid) {
      this.toast.error('Please fill in all required fields');
      return;
    }

    this.updateRulesetFromFormValues();

    this.isSaving.set(true);

    switch (this.mode()) {
      case 'create':
      case 'apply':
        this.propertyRulesetService.createCalendarRuleset(this.ruleset()).subscribe({
          next: ({ data } = {}) => {
            this.isSaving.set(false);
            this.toast.success('Rule-set has been created.');
            this.dialogRef.close({ action: RuleSetDialogAction.CREATE, data });
          },
          error: () => {
            this.isSaving.set(false);
            this.toast.error('Failed to save ruleset');
          },
        });
        return;
      case 'edit':
        this.propertyRulesetService.updateCalendarRuleset(this.uuid(), this.ruleset()).subscribe({
          next: () => {
            this.isSaving.set(false);
            this.toast.success('Rule-set has been updated.');
            this.dialogRef.close({ action: RuleSetDialogAction.UPDATE, data: this.ruleset() });
          },
          error: () => {
            this.isSaving.set(false);
            this.toast.error('Failed to save ruleset');
          },
        });
        return;
    }
  }

  backIconClicked() {
    if (this.activeFormArea() === null) {
      this.dialogRef.close({ action: RuleSetDialogAction.CANCEL });
    } else {
      this.resetActiveFormAreaAndClearUnsavedChanges(this.activeFormArea());
    }
  }

  cancel() {
    this.dialogRef.close({ action: RuleSetDialogAction.CANCEL });
  }

  deleteRuleset() {
    if (!this.deletePrompt()) {
      return;
    }

    this.isDeleting.set(true);

    this.propertyRulesetService.deleteCalendarRuleset(this.uuid()).subscribe({
      next: () => {
        this.toast.success('Rule-set deleted.');
        this.isDeleting.set(false);
        this.dialogRef.close({ action: RuleSetDialogAction.DELETE, data: { uuid: this.uuid() } });
      },
      error: () => {
        this.isDeleting.set(false);
        this.toast.error('Failed to delete ruleset');
      },
    });
  }

  // TODO: Do better than a prompt
  deletePrompt(): boolean {
    return confirm('Are you sure you want to delete this ruleset? This action cannot be undone.');
  }

  toggleRestrictedCheckInOut(field: 'check_in' | 'check_out', index: number) {
    const array = this.rulesetForm.get(`restricted_check_in_out.${field}`) as FormArray;
    const currentValues = array.value as number[];
    const isCurrentlyChecked = currentValues.includes(index);

    if (isCurrentlyChecked) {
      const valueIndex = currentValues.indexOf(index);
      if (valueIndex > -1) {
        array.removeAt(valueIndex);
      }
    } else {
      array.push(new FormControl(index));
    }
  }

  onRestrictedCheckInOutChange(field: 'check_in' | 'check_out', event: MatLegacyCheckboxChange, index: number) {
    const array = this.rulesetForm.get(`restricted_check_in_out.${field}`) as FormArray;
    const checked = event.checked;

    if (checked) {
      array.push(new FormControl(index));
    } else {
      // find the value in the array
      const value = array.value.find((value) => value === index);

      if (value !== undefined) {
        array.removeAt(array.value.indexOf(value));
      }
    }
  }

  getPriceAdjustmentSubline(seasonalAdjustment: { value: number; type: string; min_price: number; max_price: number }) {
    if (seasonalAdjustment.value == 0 || seasonalAdjustment.value == null) {
      return 'Apply a percentage increase or decrease on your nightly price.';
    }

    return `${seasonalAdjustment.value}% ${seasonalAdjustment.type}`;
  }

  getDiscountsSubline(discounts: PropertyRuleset['discounts']) {
    let discountsLength = 0;
    const discountsPrefix = `Airbnb and Direct only. `;

    if (discounts.length_of_stay?.length > 0 && discounts.length_of_stay.some((discount) => discount.nights !== null)) {
      discountsLength += discounts.length_of_stay.length;
    }

    if (discounts.last_minute?.length > 0 && discounts.last_minute.some((discount) => discount.days !== null)) {
      discountsLength += discounts.last_minute.length;
    }

    if (discounts.early_bird?.length > 0 && discounts.early_bird.some((discount) => discount.months !== null)) {
      discountsLength += discounts.early_bird.length;
    }

    if (discountsLength > 0) {
      return `${discountsPrefix} ${discountsLength} discount${discountsLength > 1 ? 's' : ''} set.`;
    }

    return `${discountsPrefix}No discounts set.`;
  }

  getMinMaxStaySubline(minMaxStay: { min: number; max: number; min_nights_by_day: (number | null)[] }) {
    if (minMaxStay.min_nights_by_day?.length && minMaxStay.min_nights_by_day.some((day) => Boolean(day))) {
      return `Customised by day.`;
    }

    if (minMaxStay.min && minMaxStay.max) {
      return `${minMaxStay.min} - ${minMaxStay.max} nights.`;
    }

    if (minMaxStay.min) {
      return `${minMaxStay.min} nights minimum.`;
    }

    if (minMaxStay.max) {
      return `${minMaxStay.max} nights maximum.`;
    }

    return 'No min/max stay set.';
  }

  getRestrictedCheckInOutSubline(restrictedCheckInOut: PropertyRuleset['restricted_check_in_out']) {
    if (restrictedCheckInOut.check_in?.length && restrictedCheckInOut.check_out?.length) {
      return `Restricted check-in/check-out days set.`;
    }

    if (restrictedCheckInOut.check_in?.length) {
      return `Restricted check-in days set.`;
    }

    if (restrictedCheckInOut.check_out?.length) {
      return `Restricted check-out days set.`;
    }

    return 'No restrictions set.';
  }

  hasOneCustomizedMinStayByDay(minMaxStay: { min_nights_by_day: (number | null)[] }) {
    return minMaxStay.min_nights_by_day?.length && minMaxStay.min_nights_by_day.some((day) => Boolean(day));
  }
}

// If both min and max stay have values, max must be greater than min
export const minMaxStayValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const minControl = control.get('min');
  const maxControl = control.get('max');

  // Get raw values
  const minRaw = minControl?.value;
  const maxRaw = maxControl?.value;

  // Check if values are null, undefined, or just whitespace
  const isMinEffectivelyEmpty = minRaw === null || minRaw === undefined || String(minRaw).trim() === '';
  const isMaxEffectivelyEmpty = maxRaw === null || maxRaw === undefined || String(maxRaw).trim() === '';

  // If either field is effectively empty, this specific validation doesn't apply
  if (isMinEffectivelyEmpty || isMaxEffectivelyEmpty) {
    return null; // No 'maxLessThanMin' error
  }

  // Attempt conversion to numbers only *after* checking they aren't empty
  const min = Number(minRaw);
  const max = Number(maxRaw);

  // Also check if the conversion resulted in NaN (e.g., user typed non-numeric text)
  if (isNaN(min) || isNaN(max)) {
    // Treat non-numeric input as not applicable for this specific validation
    return null;
  }

  // Now perform the comparison only if both were valid, non-empty numbers
  if (max < min) {
    return { maxLessThanMin: true }; // Error: Max is less than Min
  }

  // Otherwise, the validation passes (or doesn't apply)
  return null;
};

// Define the new validator function
export const lengthOfStayDiscountValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
  const nightsControl = group.get('nights');
  const valueControl = group.get('value');

  if (!nightsControl || !valueControl) {
    return null; // Should not happen if controls are defined
  }

  const nightsValue = nightsControl.value;
  const valueValue = valueControl.value;

  const nightsFilled = nightsValue !== null && nightsValue !== '' && !isNaN(Number(nightsValue));
  const valueFilled = valueValue !== null && valueValue !== '' && !isNaN(Number(valueValue));

  // If the group has other errors (like pattern or min/max on individual controls), don't add these specific errors.
  // Let the individual control validators handle it first.
  if ((nightsFilled && nightsControl.invalid) || (valueFilled && valueControl.invalid)) {
    return null;
  }

  if (nightsFilled && !valueFilled) {
    return { valueRequiredWhenNightsFilled: true };
  }

  if (!nightsFilled && valueFilled) {
    return { nightsRequiredWhenValueFilled: true };
  }

  return null;
};
